"use client";

import type { ImgHTMLAttributes, ReactNode } from "react";
import { useLocale } from "use-intl";

import { cn } from "@/lib/utils";
import { formatCountry, getFlagUrl } from "@garimpa/lib/countries";

type CountryFlagSize = "xs" | "sm" | "md" | "cover";

const COUNTRY_FLAG_SIZE_CONFIG = {
  xs: { sourceSize: 40, width: 14, height: 10, className: "rounded-sm" },
  sm: { sourceSize: 40, width: 16, height: 12, className: "rounded-sm" },
  md: { sourceSize: 40, width: 20, height: 15, className: "rounded-sm" },
  cover: {
    sourceSize: "svg",
    width: undefined,
    height: undefined,
    className: "absolute inset-0 h-full w-full object-cover",
  },
} as const satisfies Record<
  CountryFlagSize,
  {
    className: string;
    height: number | undefined;
    sourceSize: 40 | "svg";
    width: number | undefined;
  }
>;

interface CountryFlagProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "alt" | "decoding" | "height" | "loading" | "src" | "width"
> {
  code?: string | null;
  decorative?: boolean;
  fallback?: ReactNode;
  label?: string;
  size?: CountryFlagSize;
}

export function CountryFlag({ code, ...props }: CountryFlagProps) {
  if (!code) return null;

  return <ResolvedCountryFlag {...props} code={code} />;
}

function ResolvedCountryFlag({
  className,
  code,
  decorative = false,
  fallback = null,
  label,
  size = "md",
  ...props
}: Omit<CountryFlagProps, "code"> & { code: string }) {
  const locale = useLocale();
  const config = COUNTRY_FLAG_SIZE_CONFIG[size];
  const src = getFlagUrl(code, config.sourceSize);

  if (!src) return <>{fallback}</>;

  const alt = decorative
    ? ""
    : (label ?? formatCountry(code, locale, { includeUnknown: true }) ?? code);

  return (
    <img
      {...props}
      src={src}
      alt={alt}
      width={config.width}
      height={config.height}
      loading="lazy"
      decoding="async"
      className={cn(config.className, className)}
    />
  );
}
